<template>
    <LMarkerCluster
        ref="instance"
        :key="'AssetMarkerCluster' + key"
        :options="clusterOptions"
        @ready="handleClusterInit"
        @clusterclick="handleClusterClick"
        @clustermouseover="handleClusterMouseOver"
    >
        <slot />
    </LMarkerCluster>
</template>

<script>
import { mapGetters } from 'vuex'
import { DivIcon, Point } from 'leaflet'
import LMarkerCluster from 'vue2-leaflet-markercluster'

export default {
    components: {
        LMarkerCluster,
    },
    data() {
        return {
            key: 1,
        }
    },
    computed: {
        ...mapGetters('auth', ['isAxRoad']),
        ...mapGetters('tracker', ['isTrackerCountHigh', 'assetsById']),
        clusterOptions() {
            return {
                chunkedLoading: true,
                iconCreateFunction: this.createClusterIcon,
                maxClusterRadius: 120,
                removeOutsideVisibleBounds: false,
                showCoverageOnHover: false,
                spiderfyDistanceMultiplier: 3.5,
                spiderfyOnMaxZoom: false,
                zoomToBoundsOnClick: !this.isAxRoad,
                ...(!this.clusteringEnabled && {
                    disableClusteringAtZoom: 0,
                }),
                ...(this.isTrackerCountHigh && {
                    maxClusterRadius: 160,
                }),
            }
        },
        clusteringEnabled() {
            return this.$store.state.preferences.mapDisplayPreferences
                .clusteringEnabled
        },
    },
    watch: {
        clusteringEnabled() {
            // Key-changing workaround to force cluster options update on the
            // 'clusteringEnabled' state value change
            this.key++
        },
    },
    methods: {
        createClusterIcon(cluster) {
            const childCount = cluster.getChildCount()
            const includesErrorIcon = cluster
                .getAllChildMarkers()
                .some(item => item.options.icon.options.errorIconInstance)

            return new DivIcon({
                className: 'marker-cluster',
                html: includesErrorIcon
                    ? `<div>${childCount}<img class="marker-cluster__error-icon" src="${require('../assets/icons/fill-warning.svg')}"/></div>`
                    : `<div>${childCount}</div>`,
                iconSize: new Point(60, 60),
            })
        },
        handleClusterInit(cluster) {
            cluster._getExpandedVisibleBounds = () => cluster._map.getBounds()
        },
        handleClusterClick(event) {
            const map = event.target._map

            // Check if the cluster will zoom (expand) or not
            const canZoom =
                this.clusterOptions.zoomToBoundsOnClick &&
                map.getZoom() < map.getMaxZoom()

            if (canZoom) {
                return false
            }

            this.showMarkerList(event.layer)
        },
        handleClusterMouseOver(event) {
            if (!this.isAxRoad) {
                return false
            }
            this.showMarkerList(event.layer)
        },
        showMarkerList(cluster) {
            const childMarkers = cluster.getAllChildMarkers()

            // sort child markers by name
            childMarkers.sort((a, b) => {
                const nameA = a.getTooltip().getContent()
                const nameB = b.getTooltip().getContent()
                return nameA.localeCompare(nameB, undefined, { numeric: true })
            })

            const assetUrl = this.isAxRoad
                ? '#/map/ax-road/trucks/'
                : '#/map/assets/'

            // make each row of popupContent to look like: <icon> <name>
            const popupContent = childMarkers
                .map(marker => {
                    const name = marker.getTooltip().getContent()
                    const tracker = marker.options.tracker

                    const {
                        asset_details: {
                            color,
                            asset_type_marker,
                            asset_type_marker_ax_road,
                            sensor_data,
                        },
                    } = this.assetsById[tracker]
                    const activity =
                        (sensor_data?.driver_1_card_presence &&
                            sensor_data?.driver_1_working_state?.value) ||
                        8

                    const img = this.isAxRoad
                        ? `<div class="marker-cluster__popup-row-img marker-cluster__popup-row-img--ax-road marker-cluster__popup-row-img--${activity}"><img src="${asset_type_marker_ax_road}" /></div>`
                        : `<div class="marker-cluster__popup-row-img" style="border: 2px solid ${color};"><img src="${asset_type_marker}" /></div>`

                    return `<a href="${assetUrl}${tracker}" class="marker-cluster__popup-row">
                                ${img}
                                <span class="marker-cluster__popup-row-name">${name}</span>
                            </a>`
                })
                .join('')

            cluster
                .bindPopup(popupContent, {
                    offset: new Point(0, -20),
                    minWidth: 300,
                    maxHeight: 300,
                    autoPan: false,
                    autoClose: this.isAxRoad,
                    className: 'marker-cluster__popup',
                    closeButton: false,
                })
                .openPopup()
        },
    },
}
</script>

<style lang="scss">
@import '~leaflet.markercluster/dist/MarkerCluster.css';

.marker-cluster {
    &::before,
    &::after {
        content: '';
        position: absolute;
        top: 0;
        clip-path: polygon(50% 0%, 95% 25%, 95% 75%, 50% 100%, 5% 75%, 5% 25%);
    }

    &::before {
        width: 100%;
        height: 100%;
        background-color: rgba(lighten($color-primary-text, 50%), 0.5);
    }

    &::after {
        margin: 7.5%;
        width: 85%;
        height: 85%;
        background-color: $color-primary-text;
    }

    & > div {
        position: absolute;
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 20px;
        font-weight: 700;
        font-family: 'IBM Plex Sans', sans-serif;
        color: #fff;
        z-index: 1;
    }

    &__error-icon {
        width: 18px;
        height: 18px;
        position: absolute;
        top: 0;
        right: 8px;
        border-radius: 50%;
        background-color: #fff;
        padding: 2px;
    }

    &__popup {
        .leaflet-popup-content {
            margin: 0;
        }

        .leaflet-popup-scrolled {
            border: none;
        }
    }

    &__popup-row {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 16px;
        font-size: 17.55px;
        font-weight: 600;
        font-family: 'IBM Plex Sans', sans-serif;
        color: $color-primary-text !important;

        &:hover {
            background-color: $color-gray-lighter;
        }

        &-img {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 42px;
            min-width: 42px;
            height: 42px;
            border-radius: 50%;
            box-shadow: rgba(0, 0, 0, 0.4) 0px 3px 5px 0px;

            img {
                width: 21px;
                height: 21px;
            }
            &--0,
            &--1,
            &--2,
            &--3 {
                background-color: #2d79e0;
            }
            &--6,
            &--7 {
                background-color: #e3573a;
            }
            &--8 {
                background-color: #999999;
            }

            &--ax-road {
                box-shadow: none !important;
            }
        }

        &-name {
            text-overflow: ellipsis;
        }
    }
}
</style>
